import { useEffect, useState } from "react";

import {
  attachStoragePolicy,
  bytesToGb,
  createProject,
  gbToBytes,
  listProjects,
  listStoragePolicies,
  setProjectQuota,
  type ProjectRow,
  type StoragePolicy,
} from "../api";

/**
 * भाग E4 provisioning: create projects and change their storage quota from
 * the dashboard — thin UI over `POST /api/v1/admin/projects` and
 * `PUT /api/v1/admin/projects/{id}/quota` (`admin_api.routes.projects`),
 * gated server-side by `PROJECT_MANAGE`. Quotas are entered here in GB and
 * converted to bytes (`gbToBytes`, 1 GB = 1024³ bytes) before they ever
 * leave the browser — the API itself only ever sees bytes. The storage
 * policy column attaches one of the Storage Policies (where the project's
 * bytes may live); the server refuses one a project's privacy class cannot
 * satisfy, and never moves bytes that are already stored.
 */
export function Projects(): JSX.Element {
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [name, setName] = useState("");
  const [quotaGb, setQuotaGb] = useState(100);

  const [quotaEdits, setQuotaEdits] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [policies, setPolicies] = useState<StoragePolicy[]>([]);

  function reload(): void {
    listProjects()
      .then(setProjects)
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
    listStoragePolicies()
      .then(setPolicies)
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }

  async function handleSetPolicy(projectId: string, policyId: string): Promise<void> {
    setBusyId(projectId);
    setError(null);
    setNotice(null);
    try {
      await attachStoragePolicy(projectId, policyId || null);
      setNotice("Storage policy changed. It governs new uploads; objects already stored stay where they are.");
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }

  useEffect(reload, []);

  async function handleCreate(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!name.trim() || quotaGb < 0) return;
    setBusy(true);
    setError(null);
    try {
      await createProject(name.trim(), gbToBytes(quotaGb));
      setName("");
      setQuotaGb(100);
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleSetQuota(projectId: string): Promise<void> {
    const raw = quotaEdits[projectId];
    if (raw === undefined || raw === "") return;
    const gb = Number(raw);
    if (Number.isNaN(gb) || gb < 0) {
      setError("Quota must be a non-negative number of GB.");
      return;
    }
    setBusyId(projectId);
    setError(null);
    try {
      const updated = await setProjectQuota(projectId, gbToBytes(gb));
      setQuotaEdits((prev) => ({ ...prev, [projectId]: "" }));
      // Warn-and-allow: the change is applied, and the server says what it
      // means. Lowering below usage deletes nothing but refuses every new
      // upload, which an operator would otherwise read as an outage.
      setNotice(updated.quota_warnings?.length ? updated.quota_warnings.join(" ") : null);
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div>
      <h2 className="page-title">🏢 Projects</h2>
      <p className="page-subtitle">Create tenants and set their storage quota (GB → bytes).</p>
      {error && <div className="error-banner">{error}</div>}
      {notice && <div className="notice-banner">{notice}</div>}

      <div className="panel">
        <h3>Create project</h3>
        <form onSubmit={(e) => void handleCreate(e)}>
          <div className="form-row">
            <label htmlFor="new-project-name">Name</label>
            <input
              id="new-project-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="form-row">
            <label htmlFor="new-project-quota">Quota (GB)</label>
            <input
              id="new-project-quota"
              type="number"
              min={0}
              value={quotaGb}
              onChange={(e) => setQuotaGb(Number(e.target.value))}
              required
            />
          </div>
          <button type="submit" disabled={busy || !name.trim()}>
            {busy ? "Creating…" : "Create project"}
          </button>
        </form>
      </div>

      <div className="panel">
        <h3>All projects</h3>
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Used / Quota</th>
              <th>Frozen</th>
              <th>Storage policy</th>
              <th>Set quota (GB)</th>
            </tr>
          </thead>
          <tbody>
            {projects.map((p) => (
              <tr key={p.id}>
                <td>
                  {p.name}
                  <br />
                  <span className="page-subtitle" style={{ fontSize: 11 }}>
                    {p.id}
                  </span>
                </td>
                <td>
                  {p.used_bytes.toLocaleString()} / {p.quota_bytes.toLocaleString()} (
                  {bytesToGb(p.quota_bytes).toFixed(1)} GB)
                </td>
                <td>
                  <span className="badge">{p.frozen ? "frozen" : "active"}</span>
                </td>
                <td>
                  <select
                    aria-label={`Storage policy for ${p.name}`}
                    disabled={busyId === p.id}
                    value={p.storage_policy_id ?? ""}
                    onChange={(e) => void handleSetPolicy(p.id, e.target.value)}
                  >
                    <option value="">platform default</option>
                    {policies.map((pol) => (
                      <option key={pol.id} value={pol.id}>
                        {pol.name} ({pol.allowed_locations.join("+")})
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    type="number"
                    min={0}
                    style={{ width: 90 }}
                    placeholder={bytesToGb(p.quota_bytes).toFixed(1)}
                    value={quotaEdits[p.id] ?? ""}
                    onChange={(e) =>
                      setQuotaEdits((prev) => ({ ...prev, [p.id]: e.target.value }))
                    }
                  />{" "}
                  <button
                    disabled={busyId === p.id || !quotaEdits[p.id]}
                    onClick={() => void handleSetQuota(p.id)}
                  >
                    Set
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
